import React, { useEffect, useMemo, useState } from 'react';
import StepIndicator from '../components/StepIndicator';
import LoadingModal from '../components/LoadingModal';
import useSEO from '../hooks/useSEO';
import { createSubscription, formatNaira } from '../lib/data';
import { fetchFees } from '../hooks/useProductCatalog';
import { GiftOrder, OrderItem, RecipientInfo, SenderInfo, Vendor } from '../types';

interface SubscriptionCheckoutPageProps {
  items: OrderItem[];
  vendor: Vendor | null;
  sender: SenderInfo;
  recipient: RecipientInfo;
  subscription?: GiftOrder['subscription'];
  onSenderChange: (data: SenderInfo) => void;
  onRecipientChange: (data: RecipientInfo) => void;
  onSubscriptionChange: (data: GiftOrder['subscription']) => void;
  onBack: () => void;
}

interface FormErrors {
  fullName?: string;
  phone?: string;
  email?: string;
  address?: string;
  businessAddress?: string;
  sellingSpotName?: string;
  deliveryFrequency?: string;
  deliveryDays?: string;
  deliveryTimeSlot?: string;
  dropOffDay?: string;
  terms?: string;
}

interface SubscriptionCheckoutDraft {
  fullName: string;
  phone: string;
  email: string;
  address: string;
  businessAddress: string;
  sellingSpotName: string;
  message: string;
  deliveryFrequency: string;
  deliveryDays: string[];
  deliveryTimeSlot: string;
  dropOffDay: string;
}

type B2BPaymentChoice = 'daily' | 'monthly';

const DELIVERY_FREQUENCIES = ['Daily', 'Every Other Day'] as const;

const DELIVERY_TIME_SLOTS = [
  '6-8 AM',
  '3-5 PM',
] as const;

const WEEKDAY_OPTIONS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

const getSelectedWeekdayCountForCurrentMonth = (selectedDays: string[]): number => {
  if (selectedDays.length === 0) {
    return 0;
  }

  const dayNameToIndex: Record<string, number> = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };

  const selectedIndexes = new Set(
    selectedDays
      .map((day) => dayNameToIndex[day])
      .filter((value): value is number => typeof value === 'number')
  );

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

  let count = 0;

  for (let day = 1; day <= lastDayOfMonth; day += 1) {
    const date = new Date(year, month, day);
    if (selectedIndexes.has(date.getDay())) {
      count += 1;
    }
  }

  return count;
};

export default function SubscriptionCheckoutPage({
  items,
  vendor,
  sender,
  recipient,
  subscription,
  onSenderChange,
  onRecipientChange,
  onSubscriptionChange,
  onBack,
}: SubscriptionCheckoutPageProps) {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Preparing your subscription...');
  const [isDeliveryDaysOpen, setIsDeliveryDaysOpen] = useState(false);
  const [isPaymentChoiceOpen, setIsPaymentChoiceOpen] = useState(false);
  const [selectedPaymentChoice, setSelectedPaymentChoice] = useState<B2BPaymentChoice>('daily');
  const [draftForm, setDraftForm] = useState<SubscriptionCheckoutDraft>({
    fullName: sender.fullName || recipient.fullName || '',
    phone: sender.phone || recipient.phone || '',
    email: sender.email || recipient.email || '',
    address: recipient.address || '',
    businessAddress: recipient.businessAddress || '',
    sellingSpotName: recipient.sellingSpotName || '',
    message: subscription?.category === 'b2b' ? '' : recipient.message || '',
    deliveryFrequency: subscription?.deliveryFrequency || '',
    deliveryDays: subscription?.deliveryDays || [],
    deliveryTimeSlot: subscription?.deliveryTimeSlot || '',
    dropOffDay: subscription?.preferredDropoffDay || '',
  });

  useSEO({
    title: 'Checkout - BOQBOX',
    description: 'Review and complete your subscription checkout.',
  });

  useEffect(() => {
    setDraftForm({
      fullName: sender.fullName || recipient.fullName || '',
      phone: sender.phone || recipient.phone || '',
      email: sender.email || recipient.email || '',
      address: recipient.address || '',
      businessAddress: recipient.businessAddress || '',
      sellingSpotName: recipient.sellingSpotName || '',
      message: subscription?.category === 'b2b' ? '' : recipient.message || '',
      deliveryFrequency: subscription?.deliveryFrequency || '',
      deliveryDays: subscription?.deliveryDays || [],
      deliveryTimeSlot: subscription?.deliveryTimeSlot || '',
      dropOffDay: subscription?.preferredDropoffDay || '',
    });
  }, [
    recipient.address,
    recipient.businessAddress,
    recipient.email,
    recipient.fullName,
    recipient.message,
    recipient.phone,
    recipient.sellingSpotName,
    sender.email,
    sender.fullName,
    sender.phone,
    subscription?.deliveryDays,
    subscription?.deliveryFrequency,
    subscription?.deliveryTimeSlot,
    subscription?.category,
    subscription?.preferredDropoffDay,
  ]);

  useEffect(() => {
    setSubmissionError(null);
  }, [
    acceptedTerms,
    draftForm.address,
    draftForm.businessAddress,
    draftForm.deliveryDays,
    draftForm.deliveryFrequency,
    draftForm.deliveryTimeSlot,
    draftForm.email,
    draftForm.fullName,
    draftForm.message,
    draftForm.phone,
    draftForm.sellingSpotName,
    draftForm.dropOffDay,
  ]);

  useEffect(() => {
    if (!(subscription?.category === 'b2b' && draftForm.deliveryFrequency === 'Every Other Day')) {
      setIsDeliveryDaysOpen(false);
    }
  }, [draftForm.deliveryFrequency, subscription?.category]);

  const packageMetaById = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<NonNullable<GiftOrder['subscription']>['packageItems']>[number]
    >();

    for (const packageItem of subscription?.packageItems ?? []) {
      map.set(packageItem.item._id, packageItem);
    }

    return map;
  }, [subscription?.packageItems]);

  const recurringTotal = useMemo(
    () =>
      items.reduce((sum, currentItem) => {
        const unitPrice = currentItem.subscriptionOption?.price ?? currentItem.item.price ?? 0;
        return sum + unitPrice * currentItem.quantity;
      }, 0),
    [items]
  );

  const recurringLabel = subscription?.frequency ? `/${subscription.frequency}` : '';

  // The server adds service + payment fees to today's charge; show the exact amount.
  const [todayFees, setTodayFees] = useState<{ fees: number; total: number } | null>(null);
  useEffect(() => {
    let live = true;
    fetchFees(recurringTotal).then((data) => live && setTodayFees(data));
    return () => { live = false; };
  }, [recurringTotal]);
  const isB2BSubscription = subscription?.category === 'b2b';
  const isEveryOtherDayB2B =
    isB2BSubscription && draftForm.deliveryFrequency === 'Every Other Day';
  const hasSundayDelivery = useMemo(
    () =>
      isB2BSubscription
        ? draftForm.deliveryDays.includes('Sunday')
        : draftForm.dropOffDay === 'Sunday',
    [draftForm.deliveryDays, draftForm.dropOffDay, isB2BSubscription]
  );

  const b2bMonthlyDeliveryCount = useMemo(() => {
    if (!isB2BSubscription) {
      return 0;
    }

    if (draftForm.deliveryFrequency === 'Every Other Day') {
      return getSelectedWeekdayCountForCurrentMonth(draftForm.deliveryDays);
    }

    return 26;
  }, [draftForm.deliveryDays, draftForm.deliveryFrequency, isB2BSubscription]);

  const b2bProjectedMonthlyTotal = useMemo(
    () => recurringTotal * b2bMonthlyDeliveryCount,
    [b2bMonthlyDeliveryCount, recurringTotal]
  );

  const requiresDropOffDay = Boolean(subscription) && !isB2BSubscription;
  const b2bDailyPayableTotal = recurringTotal;
  const b2bMonthlyPayableTotal = b2bProjectedMonthlyTotal;

  const validate = (): boolean => {
    const nextErrors: FormErrors = {};

    if (!draftForm.fullName.trim()) {
      nextErrors.fullName = 'Full name is required';
    }

    if (!draftForm.phone.trim()) {
      nextErrors.phone = 'Phone number is required';
    } else if (draftForm.phone.replace(/\D/g, '').length < 9) {
      nextErrors.phone = 'Enter a valid phone number';
    }

    if (!draftForm.email.trim()) {
      nextErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draftForm.email)) {
      nextErrors.email = 'Enter a valid email address';
    }

    if (!draftForm.address.trim()) {
      nextErrors.address = 'Delivery address is required';
    }

    if (isB2BSubscription && !draftForm.businessAddress.trim()) {
      nextErrors.businessAddress = 'Business address is required';
    }

    if (isB2BSubscription && !draftForm.sellingSpotName.trim()) {
      nextErrors.sellingSpotName = 'Selling spot name is required';
    }

    if (isB2BSubscription && !draftForm.deliveryFrequency) {
      nextErrors.deliveryFrequency = 'Please choose a delivery frequency';
    }

    if (
      isB2BSubscription &&
      draftForm.deliveryFrequency === 'Every Other Day' &&
      draftForm.deliveryDays.length === 0
    ) {
      nextErrors.deliveryDays = 'Please choose the weekdays for this delivery schedule';
    }

    if (isB2BSubscription && !draftForm.deliveryTimeSlot) {
      nextErrors.deliveryTimeSlot = 'Please choose a delivery time slot';
    }

    if (requiresDropOffDay && !draftForm.dropOffDay) {
      nextErrors.dropOffDay = 'Please choose your preferred drop-off day';
    }

    if (!acceptedTerms) {
      nextErrors.terms = 'Please accept the terms before continuing';
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setSubmissionError('Complete the required fields and accept the terms to continue.');
      window.requestAnimationFrame(() => {
        const firstInvalidField = document.querySelector(
          '.subscription-checkout-page .form-input.error, .subscription-checkout-page .subscription-days-trigger.error, .subscription-terms-card'
        );
        firstInvalidField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    return Object.keys(nextErrors).length === 0;
  };

  const commitDraftToOrder = (paymentMode?: B2BPaymentChoice) => {
    onSenderChange({
      ...sender,
      fullName: draftForm.fullName,
      phone: draftForm.phone,
      email: draftForm.email,
    });

    onRecipientChange({
      ...recipient,
      fullName: draftForm.fullName,
      phone: draftForm.phone,
      email: draftForm.email,
      address: draftForm.address,
      businessAddress: draftForm.businessAddress || undefined,
      sellingSpotName: draftForm.sellingSpotName || undefined,
      ...(!isB2BSubscription ? { message: draftForm.message } : {}),
    });

    if (subscription) {
      onSubscriptionChange({
        ...subscription,
        paymentMode: isB2BSubscription ? paymentMode ?? subscription.paymentMode : undefined,
        deliveryFrequency: draftForm.deliveryFrequency || undefined,
        deliveryDays: draftForm.deliveryDays.length > 0 ? draftForm.deliveryDays : undefined,
        deliveryTimeSlot: draftForm.deliveryTimeSlot || undefined,
        preferredDropoffDay: draftForm.dropOffDay || undefined,
      });
    }
  };

  const submitSubscription = async (
    totalAmount: number,
    paymentMode?: B2BPaymentChoice
  ) => {
    setSubmissionError(null);
    setIsLoading(true);
    setLoadingMessage('Creating your subscription...');
    const normalizedEmail = draftForm.email.trim();

    const payload = {
      name: subscription?.tierLabel || 'Subscription Plan',
      type: 'self' as const,
      category: subscription?.backendCategory || 'general',
      frequency: subscription?.frequency || 'monthly',
      subscribedBy: {
        name: draftForm.fullName,
        email_address: normalizedEmail,
        phone_number: draftForm.phone,
      },
      subscribedFor: {
        name: draftForm.fullName,
        email_address: normalizedEmail,
        phone_number: draftForm.phone,
        delivery_address: draftForm.address,
        ...(isB2BSubscription && draftForm.businessAddress.trim()
          ? { business_address: draftForm.businessAddress.trim() }
          : {}),
        ...(isB2BSubscription && draftForm.sellingSpotName.trim()
          ? { selling_spot_name: draftForm.sellingSpotName.trim() }
          : {}),
      },
      ...(vendor?.name?.trim() ? { vendor: vendor.name.trim() } : {}),
      items: items.map((orderItem) => {
        const itemId = orderItem.item._id || orderItem.item.id || '';
        const packageMeta = packageMetaById.get(itemId);
        return {
          item: itemId,
          item_name: orderItem.item.name,
          quantity: orderItem.quantity,
          type: packageMeta?.type,
          color: packageMeta?.color,
          size: packageMeta?.size,
          brand: packageMeta?.brand,
        };
      }),
      ...(isB2BSubscription && paymentMode ? { paymentMode } : {}),
      ...(isB2BSubscription && draftForm.deliveryFrequency
        ? { deliveryFrequency: draftForm.deliveryFrequency.toLowerCase() }
        : {}),
      ...(isB2BSubscription && draftForm.deliveryDays.length > 0
        ? { deliveryDays: draftForm.deliveryDays.map((day) => day.toLowerCase()) }
        : {}),
      ...(isB2BSubscription && draftForm.deliveryTimeSlot
        ? { deliveryTimeSlot: draftForm.deliveryTimeSlot }
        : {}),
      ...(!isB2BSubscription && draftForm.dropOffDay
        ? { dropoffDay: draftForm.dropOffDay.toLowerCase() }
        : {}),
      message: isB2BSubscription ? '' : draftForm.message || '',
      totalAmount,
    };

    const result = await createSubscription(payload);

    if (result.success && result.payment_url) {
      commitDraftToOrder(paymentMode);
      setLoadingMessage('Redirecting to payment...');
      window.setTimeout(() => {
        window.location.href = result.payment_url as string;
      }, 300);
      return;
    }

    setIsLoading(false);
    setSubmissionError(result.message || 'Unable to create your subscription right now.');
    window.requestAnimationFrame(() => {
      document
        .querySelector('.subscription-checkout-page .subscription-inline-alert')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const handlePlaceSubscription = async () => {
    if (!validate()) {
      return;
    }

    if (isB2BSubscription) {
      setIsPaymentChoiceOpen(true);
      return;
    }

    await submitSubscription(recurringTotal);
  };

  const handleConfirmPaymentChoice = async () => {
    const totalAmount =
      selectedPaymentChoice === 'monthly' ? b2bMonthlyPayableTotal : b2bDailyPayableTotal;

    setIsPaymentChoiceOpen(false);
    await submitSubscription(totalAmount, selectedPaymentChoice);
  };

  return (
    <div className="subscription-checkout-page page-enter">
      {isPaymentChoiceOpen ? (
        <div className="subscription-payment-choice-overlay" role="dialog" aria-modal="true">
          <div className="subscription-payment-choice-modal">
            <div className="subscription-payment-choice-copy">
              <h2>Choose how to pay</h2>
              <p>Select whether you want to pay just for today or cover the full projected month.</p>
            </div>

            <button
              type="button"
              className={`subscription-payment-choice-option ${
                selectedPaymentChoice === 'daily' ? 'selected' : ''
              }`}
              onClick={() => setSelectedPaymentChoice('daily')}
            >
              <div>
                <strong>Pay just today</strong>
                <span>Charge only the daily delivery amount.</span>
              </div>
              <b>{formatNaira(b2bDailyPayableTotal)}</b>
            </button>

            <button
              type="button"
              className={`subscription-payment-choice-option ${
                selectedPaymentChoice === 'monthly' ? 'selected' : ''
              }`}
              onClick={() => setSelectedPaymentChoice('monthly')}
            >
              <div>
                <strong>Pay full month</strong>
                <span>
                  Charge the projected monthly total ({b2bMonthlyDeliveryCount} deliveries).
                </span>
              </div>
              <b>{formatNaira(b2bMonthlyPayableTotal)}</b>
            </button>

            <div className="subscription-payment-choice-actions">
              <button
                type="button"
                className="subscription-payment-choice-cancel"
                onClick={() => setIsPaymentChoiceOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="subscription-payment-choice-confirm"
                onClick={() => {
                  void handleConfirmPaymentChoice();
                }}
              >
                Continue to Payment
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <LoadingModal
        isOpen={isLoading}
        message={loadingMessage}
        subMessage={loadingMessage === 'Creating your subscription...' ? 'Please wait...' : 'Almost there...'}
      />

      <div className="subscription-checkout-content">
        <div className="subscription-customize-header">
          <button
            type="button"
            className="subscription-back-button"
            onClick={() => {
              commitDraftToOrder();
              onBack();
            }}
            aria-label="Go back"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div>
            <h1 className="subscription-customize-title">Checkout</h1>
            <p className="subscription-customize-subtitle">Review and complete your subscription</p>
          </div>
        </div>

        <StepIndicator totalSteps={4} currentStep={3} />

        {submissionError ? <div className="subscription-inline-alert"><span>{submissionError}</span></div> : null}

        <section className="subscription-checkout-card">
          <h2>Order Summary</h2>

          <div className="subscription-checkout-lines">
            {items.map((orderItem) => {
              const unitPrice = orderItem.subscriptionOption?.price ?? orderItem.item.price ?? 0;
              return (
                <div key={orderItem.item._id || orderItem.item.id} className="subscription-checkout-line">
                  <div>
                    <strong>{orderItem.item.name}</strong>
                    <span>Qty: {orderItem.quantity}</span>
                  </div>
                  <b>{formatNaira(unitPrice * orderItem.quantity)}</b>
                </div>
              );
            })}
          </div>

          <div className="subscription-checkout-line total">
            <div>
              <strong>Vendor</strong>
              <span>{vendor?.address || 'Selected vendor'}</span>
            </div>
            <b>{vendor?.name || 'Not selected'}</b>
          </div>

          <div className="subscription-checkout-line total">
            <div>
              <strong>
                {isB2BSubscription
                  ? `Monthly total (${b2bMonthlyDeliveryCount} deliveries)`
                  : subscription?.frequency
                    ? `${subscription.frequency} total`
                    : 'Total'}
              </strong>
            </div>
            <b className="highlight">
              {isB2BSubscription
                ? formatNaira(b2bProjectedMonthlyTotal)
                : `${formatNaira(recurringTotal)}${recurringLabel}`}
            </b>
          </div>

          <div className="subscription-checkout-line">
            <div><strong>Service & payment fees</strong><span>Charged today</span></div>
            <b>{todayFees ? formatNaira(todayFees.fees) : '…'}</b>
          </div>

          {requiresDropOffDay && draftForm.dropOffDay ? (
            <div className="subscription-checkout-line total">
              <div>
                <strong>Preferred Drop-off Day</strong>
              </div>
              <b>{draftForm.dropOffDay}</b>
            </div>
          ) : null}

          {isB2BSubscription && draftForm.deliveryFrequency ? (
            <div className="subscription-checkout-line total">
              <div>
                <strong>Delivery Frequency</strong>
              </div>
              <b>{draftForm.deliveryFrequency}</b>
            </div>
          ) : null}

          {isB2BSubscription && draftForm.deliveryFrequency === 'Every Other Day' && draftForm.deliveryDays.length > 0 ? (
            <div className="subscription-checkout-line total">
              <div>
                <strong>Delivery Days</strong>
              </div>
              <b>{draftForm.deliveryDays.join(', ')}</b>
            </div>
          ) : null}

          {isB2BSubscription && draftForm.deliveryTimeSlot ? (
            <div className="subscription-checkout-line total">
              <div>
                <strong>Delivery Time Slot</strong>
              </div>
              <b>{draftForm.deliveryTimeSlot}</b>
            </div>
          ) : null}
        </section>

        <section className="subscription-checkout-card">
          <h2>Your Information</h2>

          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input
              type="text"
              className={`form-input ${errors.fullName ? 'error' : ''}`}
              placeholder="Enter your full name"
              value={draftForm.fullName}
              onChange={(event) =>
                setDraftForm((current) => ({ ...current, fullName: event.target.value }))
              }
            />
            {errors.fullName ? <p className="form-error">{errors.fullName}</p> : null}
          </div>

          <div className="form-group">
            <label className="form-label">Phone Number *</label>
            <input
              type="tel"
              className={`form-input ${errors.phone ? 'error' : ''}`}
              placeholder="080xxxxxxxx"
              value={draftForm.phone}
              onChange={(event) =>
                setDraftForm((current) => ({ ...current, phone: event.target.value }))
              }
            />
            {errors.phone ? <p className="form-error">{errors.phone}</p> : null}
          </div>

          <div className="form-group">
            <label className="form-label">Email Address *</label>
            <input
              type="email"
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder="your@email.com"
              value={draftForm.email}
              onChange={(event) =>
                setDraftForm((current) => ({ ...current, email: event.target.value }))
              }
            />
            {errors.email ? <p className="form-error">{errors.email}</p> : null}
          </div>

          <div className="form-group">
            <label className="form-label">
              {isB2BSubscription ? 'Exact Delivery Location *' : 'Delivery Address *'}
            </label>
            <textarea
              className={`form-input ${errors.address ? 'error' : ''}`}
              placeholder={
                isB2BSubscription
                  ? 'Specific landmarks, nearby shops, or detailed directions'
                  : 'Enter your delivery address'
              }
              rows={4}
              value={draftForm.address}
              onChange={(event) =>
                setDraftForm((current) => ({ ...current, address: event.target.value }))
              }
            />
            {errors.address ? <p className="form-error">{errors.address}</p> : null}
          </div>

          {isB2BSubscription ? (
            <>
              <div className="form-group">
                <label className="form-label">Business Address *</label>
                <textarea
                  className={`form-input ${errors.businessAddress ? 'error' : ''}`}
                  placeholder="Your business location"
                  rows={4}
                  value={draftForm.businessAddress}
                  onChange={(event) =>
                    setDraftForm((current) => ({ ...current, businessAddress: event.target.value }))
                  }
                />
                {errors.businessAddress ? <p className="form-error">{errors.businessAddress}</p> : null}
              </div>

              <div className="form-group">
                <label className="form-label">Selling Spot Name *</label>
                <input
                  type="text"
                  className={`form-input ${errors.sellingSpotName ? 'error' : ''}`}
                  placeholder="e.g., Mama Ngozi's Spot, Junction by GTBank"
                  value={draftForm.sellingSpotName}
                  onChange={(event) =>
                    setDraftForm((current) => ({ ...current, sellingSpotName: event.target.value }))
                  }
                />
                {errors.sellingSpotName ? <p className="form-error">{errors.sellingSpotName}</p> : null}
              </div>

              <div className="form-group">
                <label className="form-label">Delivery Frequency *</label>
                <select
                  className={`form-input ${errors.deliveryFrequency ? 'error' : ''}`}
                  value={draftForm.deliveryFrequency}
                  onChange={(event) =>
                    setDraftForm((current) => ({
                      ...current,
                      deliveryFrequency: event.target.value,
                    }))
                  }
                >
                  <option value="">Select a frequency</option>
                  {DELIVERY_FREQUENCIES.map((frequency) => (
                    <option key={frequency} value={frequency}>
                      {frequency}
                    </option>
                  ))}
                </select>
                {errors.deliveryFrequency ? <p className="form-error">{errors.deliveryFrequency}</p> : null}
              </div>

              {isEveryOtherDayB2B ? (
                <div className="form-group">
                  <label className="form-label">Delivery Days *</label>
                  <button
                    type="button"
                    className={`subscription-days-trigger ${errors.deliveryDays ? 'error' : ''}`}
                    onClick={() => setIsDeliveryDaysOpen((current) => !current)}
                  >
                    <span>
                      {draftForm.deliveryDays.length > 0
                        ? draftForm.deliveryDays.join(', ')
                        : 'Choose delivery days'}
                    </span>
                    <strong>{isDeliveryDaysOpen ? 'Close' : 'Select'}</strong>
                  </button>

                  {isDeliveryDaysOpen ? (
                    <div className="subscription-days-dropdown">
                      {WEEKDAY_OPTIONS.map((day) => {
                        const isSelected = draftForm.deliveryDays.includes(day);

                        return (
                          <label key={day} className={`subscription-day-option ${isSelected ? 'selected' : ''}`}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() =>
                                setDraftForm((current) => ({
                                  ...current,
                                  deliveryDays: isSelected
                                    ? current.deliveryDays.filter((currentDay) => currentDay !== day)
                                    : [...current.deliveryDays, day],
                                }))
                              }
                            />
                            <span>{day}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : null}

                  {errors.deliveryDays ? <p className="form-error">{errors.deliveryDays}</p> : null}
                  {hasSundayDelivery ? (
                    <p className="subscription-checkout-note">
                      Sunday deliveries start at 3:00 PM.
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="form-group">
                <label className="form-label">Delivery Time Slot *</label>
                <select
                  className={`form-input ${errors.deliveryTimeSlot ? 'error' : ''}`}
                  value={draftForm.deliveryTimeSlot}
                  onChange={(event) =>
                    setDraftForm((current) => ({ ...current, deliveryTimeSlot: event.target.value }))
                  }
                >
                  <option value="">Select a time slot</option>
                  {DELIVERY_TIME_SLOTS.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
                {errors.deliveryTimeSlot ? <p className="form-error">{errors.deliveryTimeSlot}</p> : null}
              </div>

              <div className="subscription-checkout-discreet-note">
                <div className="subscription-checkout-discreet-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 3L19 6V11C19 15.4183 16.134 19.4414 12 21C7.866 19.4414 5 15.4183 5 11V6L12 3Z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.5 11.5L11.2 13.2L14.8 9.6"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="subscription-checkout-discreet-copy">
                  <strong>Discreet Delivery Guaranteed</strong>
                  <p>
                    All items delivered in plain, unlabelled bags by BOQBOX Queens. Your business
                    remains professional and private.
                  </p>
                </div>
              </div>
            </>
          ) : null}

          {requiresDropOffDay ? (
            <div className="form-group">
              <label className="form-label">Preferred Drop-off Day *</label>
              <select
                className={`form-input ${errors.dropOffDay ? 'error' : ''}`}
                value={draftForm.dropOffDay}
                onChange={(event) =>
                  setDraftForm((current) => ({ ...current, dropOffDay: event.target.value }))
                }
              >
                <option value="">Select a day</option>
                {WEEKDAY_OPTIONS.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
              {errors.dropOffDay ? <p className="form-error">{errors.dropOffDay}</p> : null}
              {draftForm.dropOffDay === 'Sunday' ? (
                <p className="subscription-checkout-note">
                  Sunday deliveries start at 3:00 PM.
                </p>
              ) : null}
            </div>
          ) : null}

          {!isB2BSubscription ? (
            <div className="form-group">
              <label className="form-label">Message (optional)</label>
              <textarea
                className="form-input"
                placeholder="Add a note for your subscription"
                rows={3}
                value={draftForm.message}
                onChange={(event) =>
                  setDraftForm((current) => ({ ...current, message: event.target.value }))
                }
              />
            </div>
          ) : null}
        </section>

        <section className="subscription-terms-card">
          <label className="subscription-terms-row">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(event) => {
                setAcceptedTerms(event.target.checked);
                if (errors.terms) {
                  setErrors((current) => ({ ...current, terms: undefined }));
                }
              }}
            />
            <span>
              I agree to the terms and conditions, including one-time monthly payment for subscription
              services. I understand that deliveries will occur as scheduled and all items are non-refundable once delivered.
            </span>
          </label>
          {errors.terms ? <p className="form-error">{errors.terms}</p> : null}
        </section>
      </div>

      <div className="subscription-footer">
        <div className="subscription-footer-copy">
          <span>Total to pay today{todayFees ? ' (incl. fees)' : ''}</span>
          <strong>{formatNaira(todayFees?.total ?? recurringTotal)}</strong>
          {submissionError ? <small className="error">{submissionError}</small> : null}
        </div>

        <button type="button" className="subscription-footer-button" onClick={handlePlaceSubscription}>
          Proceed to Payment
        </button>
      </div>
    </div>
  );
}
